'use client';

import { type RefObject, useEffect } from 'react';

interface UseOzonetelVisibilityRecoveryParams {
  enabled: boolean;
  isInitializedRef: RefObject<boolean>;
  initializeOzonetel: () => Promise<void>;
}

/**
 * Revives the agent session when the user comes back to the tab. A backgrounded tab has its timers
 * throttled hard enough for Ozonetel's agent websocket to drop, and nothing in the SDK notices -
 * without this the agent looks connected but every dial is silently discarded.
 */
export function useOzonetelVisibilityRecovery({
  enabled,
  isInitializedRef,
  initializeOzonetel,
}: UseOzonetelVisibilityRecoveryParams) {
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isInitializedRef.current) return;

      void initializeOzonetel().catch((error) =>
        console.error('Failed to revalidate the Ozonetel session', error),
      );
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, initializeOzonetel, isInitializedRef]);
}
